import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../client';

function unwrapApiResponse(data: any): any {
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) return data.data;
  return data;
}

export const useMembers = (workspaceId: string) => {
  return useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => client.get('/users/members').then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useInvitations = (workspaceId: string) => {
  return useQuery({
    queryKey: ['invitations', workspaceId],
    queryFn: () => client.get('/workspace/invitations', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useRoles = (organizationId: string) => {
  return useQuery({
    queryKey: ['roles', organizationId],
    queryFn: () => client.get('/roles/organization').then(r => r.data).then(unwrapApiResponse),
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 10,
  });
};

export const usePermissions = () => {
  return useQuery({
    queryKey: ['permissions'],
    queryFn: () => client.get('/permissions').then(r => r.data).then(unwrapApiResponse),
    staleTime: 1000 * 60 * 10,
  });
};

export const useAuditLogs = (workspaceId: string, tableName?: string) => {
  return useQuery({
    queryKey: ['audit-logs', workspaceId, tableName],
    queryFn: () => client.get('/audit-logs', { params: { workspaceId, tableName } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useUpdateMemberRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, roleKey, workspaceId }: { memberId: string; roleKey: string; workspaceId: string }) =>
      client.put(`/users/members/${memberId}`, { roleKey }).then(r => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['members', vars.workspaceId] });
    },
  });
};

export const useRemoveMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, workspaceId }: { memberId: string; workspaceId: string }) =>
      client.delete(`/users/members/${memberId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['members', vars.workspaceId] });
    },
  });
};

export const useCreateInvitation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; role?: string; workspaceId: string }) =>
      client.post('/workspace/invitations', data).then(r => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['invitations', vars.workspaceId] });
    },
  });
};

export const useCancelInvitation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invitationId, workspaceId }: { invitationId: string; workspaceId: string }) =>
      client.delete(`/workspace/invitations/${invitationId}`).then(r => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['invitations', vars.workspaceId] });
    },
  });
};

export const useResendInvitation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invitationId, workspaceId }: { invitationId: string; workspaceId: string }) =>
      client.post(`/workspace/invitations/${invitationId}/resend`).then(r => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['invitations', vars.workspaceId] });
    },
  });
};

export const useUpdateWorkspace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; [key: string]: any }) =>
      client.put(`/workspaces/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      qc.invalidateQueries({ queryKey: ['workspace'] });
    },
  });
};

export const useDeleteWorkspace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.delete(`/workspaces/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      qc.invalidateQueries({ queryKey: ['workspace'] });
    },
  });
};